import {
  distance2D,
  makeId,
  polygonAreaMm2,
  snapValue,
  type Room,
  type SceneDocument,
  type Vec2,
  type Wall,
} from "@calcplan/domain";

export type SnapKind =
  | "endpoint"
  | "intersection"
  | "midpoint"
  | "orthogonal"
  | "angle"
  | "grid"
  | "none";

export interface SnapResult {
  pointMm: Vec2;
  kind: SnapKind;
  distanceMm: number;
  sourceIds: string[];
  guide?: { fromMm: Vec2; toMm: Vec2 };
}

interface SnapCandidate extends SnapResult {
  priority: number;
}

const PRIORITY: Record<Exclude<SnapKind, "none">, number> = {
  endpoint: 0,
  intersection: 1,
  midpoint: 2,
  orthogonal: 3,
  angle: 4,
  grid: 5,
};

export function resolveSnap(
  scene: SceneDocument,
  rawPointMm: Vec2,
  options: {
    radiusMm: number;
    draftStartMm?: Vec2 | null;
    previous?: SnapResult | null;
  },
): SnapResult {
  if (!scene.settings.snapEnabled) {
    return { pointMm: rawPointMm, kind: "none", distanceMm: 0, sourceIds: [] };
  }
  const candidates: SnapCandidate[] = [];
  for (const wall of scene.walls) {
    for (const point of [wall.startMm, wall.endMm] as Vec2[]) {
      addCandidate(candidates, "endpoint", point, rawPointMm, options.radiusMm, [wall.id]);
    }
    const midpoint: Vec2 = [
      (wall.startMm[0] + wall.endMm[0]) / 2,
      (wall.startMm[1] + wall.endMm[1]) / 2,
    ];
    addCandidate(candidates, "midpoint", midpoint, rawPointMm, options.radiusMm, [wall.id]);
  }
  for (let first = 0; first < scene.walls.length; first += 1) {
    for (let second = first + 1; second < scene.walls.length; second += 1) {
      const point = segmentIntersection(scene.walls[first]!, scene.walls[second]!);
      if (point) {
        addCandidate(
          candidates,
          "intersection",
          point,
          rawPointMm,
          options.radiusMm,
          [scene.walls[first]!.id, scene.walls[second]!.id],
        );
      }
    }
  }
  if (options.draftStartMm) {
    const [startX, startZ] = options.draftStartMm;
    const orthogonalCandidates: Vec2[] = [
      [rawPointMm[0], startZ],
      [startX, rawPointMm[1]],
    ];
    for (const point of orthogonalCandidates) {
      addCandidate(candidates, "orthogonal", point, rawPointMm, options.radiusMm, [], {
        fromMm: options.draftStartMm,
        toMm: point,
      });
    }
    const dx = rawPointMm[0] - startX;
    const dz = rawPointMm[1] - startZ;
    const length = Math.hypot(dx, dz);
    if (length > 0) {
      const step = (scene.settings.angularSnapDeg * Math.PI) / 180;
      const angle = Math.round(Math.atan2(dz, dx) / step) * step;
      const point: Vec2 = [startX + Math.cos(angle) * length, startZ + Math.sin(angle) * length];
      addCandidate(candidates, "angle", point, rawPointMm, options.radiusMm, [], {
        fromMm: options.draftStartMm,
        toMm: point,
      });
    }
  }
  const gridPoint: Vec2 = [
    snapValue(rawPointMm[0], scene.settings.gridSizeMm),
    snapValue(rawPointMm[1], scene.settings.gridSizeMm),
  ];
  addCandidate(candidates, "grid", gridPoint, rawPointMm, options.radiusMm * 1.5, []);
  candidates.sort((a, b) => a.priority - b.priority || a.distanceMm - b.distanceMm);
  const selected = candidates[0];
  if (!selected) return { pointMm: rawPointMm, kind: "none", distanceMm: 0, sourceIds: [] };

  if (
    options.previous &&
    options.previous.kind !== "none" &&
    distance2D(options.previous.pointMm, rawPointMm) <= options.radiusMm * 1.25
  ) {
    return options.previous;
  }
  const { priority: _priority, ...result } = selected;
  return result;
}

function addCandidate(
  candidates: SnapCandidate[],
  kind: Exclude<SnapKind, "none">,
  pointMm: Vec2,
  rawPointMm: Vec2,
  radiusMm: number,
  sourceIds: string[],
  guide?: SnapResult["guide"],
) {
  const distanceMm = distance2D(pointMm, rawPointMm);
  if (distanceMm <= radiusMm) {
    candidates.push({ pointMm, kind, distanceMm, sourceIds, guide, priority: PRIORITY[kind] });
  }
}

export function segmentIntersection(first: Wall, second: Wall): Vec2 | null {
  const [x1, y1] = first.startMm;
  const [x2, y2] = first.endMm;
  const [x3, y3] = second.startMm;
  const [x4, y4] = second.endMm;
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denominator) < 1e-9) return null;
  const determinant1 = x1 * y2 - y1 * x2;
  const determinant2 = x3 * y4 - y3 * x4;
  const x = (determinant1 * (x3 - x4) - (x1 - x2) * determinant2) / denominator;
  const y = (determinant1 * (y3 - y4) - (y1 - y2) * determinant2) / denominator;
  const within = (value: number, a: number, b: number) =>
    value >= Math.min(a, b) - 0.01 && value <= Math.max(a, b) + 0.01;
  return within(x, x1, x2) && within(y, y1, y2) && within(x, x3, x4) && within(y, y3, y4)
    ? [x, y]
    : null;
}

export function detectRooms(scene: SceneDocument): Room[] {
  const graph = new Map<string, Array<{ point: Vec2; wallId: string }>>();
  const pointByKey = new Map<string, Vec2>();
  for (const wall of scene.walls) {
    const startKey = roomEndpointKey(wall.startMm);
    const endKey = roomEndpointKey(wall.endMm);
    pointByKey.set(startKey, wall.startMm);
    pointByKey.set(endKey, wall.endMm);
    graph.set(startKey, [...(graph.get(startKey) ?? []), { point: wall.endMm, wallId: wall.id }]);
    graph.set(endKey, [...(graph.get(endKey) ?? []), { point: wall.startMm, wallId: wall.id }]);
  }

  const cycles = new Map<string, Vec2[]>();
  for (const startKey of graph.keys()) {
    walkCycles(startKey, startKey, [startKey], new Set(), graph, pointByKey, cycles);
  }
  return [...cycles.values()]
    .filter((boundary) => boundary.length >= 3)
    .sort((a, b) => polygonAreaMm2(b) - polygonAreaMm2(a))
    .map((boundary, index) => {
      const intersections = polygonSelfIntersections(boundary);
      return {
        id: scene.rooms[index]?.id ?? makeId("room"),
        levelId: "level-ground",
        layerId: scene.layers[0]?.id ?? "layer-architecture",
        name: scene.rooms[index]?.name ?? `Ambiente ${index + 1}`,
        boundary,
        holes: [],
        areaMm2: polygonAreaMm2(boundary),
        materialAssignments: scene.rooms[index]?.materialAssignments ?? {},
        valid: intersections.length === 0,
        diagnostics: intersections.length ? ["ROOM_SELF_INTERSECTION"] : [],
        extensions: scene.rooms[index]?.extensions ?? {},
      };
    });
}

function walkCycles(
  startKey: string,
  currentKey: string,
  path: string[],
  usedEdges: Set<string>,
  graph: Map<string, Array<{ point: Vec2; wallId: string }>>,
  points: Map<string, Vec2>,
  output: Map<string, Vec2[]>,
) {
  if (path.length > 32) return;
  for (const edge of graph.get(currentKey) ?? []) {
    const nextKey = roomEndpointKey(edge.point);
    const edgeKey = [currentKey, nextKey].sort().join("|");
    if (usedEdges.has(edgeKey)) continue;
    if (nextKey === startKey && path.length >= 3) {
      const cycleKeys = [...path];
      const canonical = canonicalCycle(cycleKeys);
      if (!output.has(canonical)) {
        output.set(canonical, cycleKeys.map((key) => points.get(key)!).filter(Boolean));
      }
      continue;
    }
    if (path.includes(nextKey)) continue;
    walkCycles(
      startKey,
      nextKey,
      [...path, nextKey],
      new Set([...usedEdges, edgeKey]),
      graph,
      points,
      output,
    );
  }
}

/**
 * Wall editing can leave sub-millimetre differences between two visually
 * connected endpoints. Group them in a 2 mm cell so room/floor/ceiling
 * detection remains stable without closing genuinely open wall gaps.
 */
function roomEndpointKey(point: Vec2): string {
  const stepMm = 2;
  return `${Math.round(point[0] / stepMm)}:${Math.round(point[1] / stepMm)}`;
}

function canonicalCycle(keys: string[]): string {
  const rotations = (values: string[]) =>
    values.map((_, index) => [...values.slice(index), ...values.slice(0, index)].join(">"));
  return [...rotations(keys), ...rotations([...keys].reverse())].sort()[0]!;
}

export function polygonSelfIntersections(points: Vec2[]): Vec2[] {
  const intersections: Vec2[] = [];
  const wallFrom = (start: Vec2, end: Vec2, id: string): Wall =>
    ({
      id,
      kind: "wall",
      startMm: start,
      endMm: end,
    }) as Wall;
  for (let first = 0; first < points.length; first += 1) {
    for (let second = first + 1; second < points.length; second += 1) {
      if (
        second === first ||
        second === (first + 1) % points.length ||
        first === (second + 1) % points.length
      ) {
        continue;
      }
      const point = segmentIntersection(
        wallFrom(points[first]!, points[(first + 1) % points.length]!, `a-${first}`),
        wallFrom(points[second]!, points[(second + 1) % points.length]!, `b-${second}`),
      );
      if (point) intersections.push(point);
    }
  }
  return intersections;
}

export function projectPointOnWall(point: Vec2, wall: Wall): {
  pointMm: Vec2;
  offsetMm: number;
  distanceMm: number;
} {
  const dx = wall.endMm[0] - wall.startMm[0];
  const dz = wall.endMm[1] - wall.startMm[1];
  const lengthSquared = dx * dx + dz * dz;
  const t = lengthSquared
    ? Math.max(
        0,
        Math.min(
          1,
          ((point[0] - wall.startMm[0]) * dx + (point[1] - wall.startMm[1]) * dz) /
            lengthSquared,
        ),
      )
    : 0;
  const projected: Vec2 = [wall.startMm[0] + dx * t, wall.startMm[1] + dz * t];
  return {
    pointMm: projected,
    offsetMm: Math.sqrt(lengthSquared) * t,
    distanceMm: distance2D(point, projected),
  };
}

export function resizeWallFromLength(wall: Wall, lengthMm: number): Wall {
  if (!Number.isFinite(lengthMm) || lengthMm < 100) {
    throw new Error("WALL_LENGTH_INVALID");
  }
  const angle = Math.atan2(wall.endMm[1] - wall.startMm[1], wall.endMm[0] - wall.startMm[0]);
  return {
    ...wall,
    revision: wall.revision + 1,
    endMm: [
      wall.startMm[0] + Math.cos(angle) * lengthMm,
      wall.startMm[1] + Math.sin(angle) * lengthMm,
    ],
  };
}

export function nearestWall(scene: SceneDocument, pointMm: Vec2, maxDistanceMm: number): Wall | null {
  return (
    scene.walls
      .map((wall) => ({ wall, distance: projectPointOnWall(pointMm, wall).distanceMm }))
      .filter((entry) => entry.distance <= maxDistanceMm)
      .sort((a, b) => a.distance - b.distance)[0]?.wall ?? null
  );
}
